import { PinataFDK } from "pinata-fdk";
import { FarcasterUser } from '../types/farcaster';

// Initialize Pinata FDK for Frame utilities
const fdk = new PinataFDK();

export interface FrameContext {
  user: FarcasterUser | null;
  isAuthenticated: boolean;
  fid: number | null;
}

export interface FrameActionData {
  untrustedData: {
    fid: number;
    url: string;
    messageHash: string;
    timestamp: number;
    network: number;
    buttonIndex: 1 | 2 | 3 | 4;
    inputText?: string;
    castId?: {
      fid: number;
      hash: string;
    };
  };
  trustedData: {
    messageBytes: string;
  };
}

/**
 * Get user context from Frame environment
 * In a real Frame, this would come from the Frame action payload
 */
export const getFrameUserContext = async (): Promise<FrameContext> => {
  try {
    // In a real Frame environment, we would get this from the Frame action
    // For now, we'll simulate Frame user context
    
    // Check if we're in a Frame environment
    if (typeof window !== 'undefined' && window.parent !== window) {
      // We're in an iframe (Frame environment)
      
      // Try to get Frame data from URL params or postMessage
      const urlParams = new URLSearchParams(window.location.search);
      const fidParam = urlParams.get('fid');
      
      if (fidParam) {
        const fid = parseInt(fidParam);
        
        // Get user details using Pinata FDK
        try {
          const userData = await fdk.getUserByFid(fid);
          
          const user: FarcasterUser = {
            fid: userData.fid,
            username: userData.username,
            display_name: userData.pfp || userData.username,
            pfp_url: userData.pfp || '',
            profile: {
              bio: {
                text: userData.bio || ''
              }
            },
            follower_count: 0,
            following_count: 0
          };
          
          return {
            user,
            isAuthenticated: true,
            fid
          };
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    }
    
    // Fallback: not in Frame environment or no user data
    return {
      user: null,
      isAuthenticated: false,
      fid: null
    };
  } catch (error) {
    console.error('Error getting Frame user context:', error);
    return {
      user: null,
      isAuthenticated: false,
      fid: null
    };
  }
};

/**
 * Validate Frame message (for production use)
 */
export const validateFrameMessage = async (frameData: any): Promise<boolean> => {
  try {
    const { isValid } = await fdk.validateFrameMessage(frameData);
    return isValid;
  } catch (error) {
    console.error('Error validating Frame message:', error);
    return false;
  }
};

/**
 * Create Frame metadata for the initial Frame
 */
export const createFrameMetadata = (imageUrl: string, postUrl: string) => {
  return fdk.getFrameMetadata({
    image: { url: imageUrl },
    buttons: [
      { label: 'Draw Cards', action: 'post' },
      { label: 'View Reading', action: 'post' },
      { label: 'Share', action: 'post' }
    ],
    post_url: postUrl,
    aspect_ratio: '1.91:1'
  });
};

export default {
  getFrameUserContext,
  validateFrameMessage,
  createFrameMetadata
}; 